'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  TrashIcon,
  SearchIcon,
  UserIcon,
  MonitorIcon,
  CalendarIcon,
  HashIcon,
  GlobeIcon,
  WifiIcon,
  WifiOffIcon,
  ShieldIcon,
  ClockIcon,
  TagIcon,
  ExternalLinkIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { ContentError, DeleteConfirmationDialog } from '@/components/partials'
import { useSearchLog, useDeleteSearchLog, useCopyToClipboard } from '@/hooks'
import { useMeStore } from '@/store'
import { formatDateTime } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { SearchLogFiltersType } from '@/types/search-log'
import { CategoryType } from '@/types/category'
import { VehicleGroupType } from '@/types/vehicle-group'

type PropsType = {
  id: string
}

const SearchLogShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const getCategoryName = (category: CategoryType, locale: string): string => {
  const translation = category.translations[locale]
  if (translation?.name) {
    return translation.name
  }
  const firstKey = Object.keys(category.translations)[0]
  return category.translations[firstKey]?.name || category.id
}

const getVehicleGroupTitle = (group: VehicleGroupType, locale: string): string => {
  const translation = group.translations[locale]
  if (translation?.title) {
    return translation.title
  }
  const firstKey = Object.keys(group.translations)[0]
  return (group.translations[firstKey] as any)?.title || group.id
}

const EntityBadge = ({ id, name, href, canLink }: { id: string; name?: string; href: string; canLink: boolean }) => {
  const content = (
    <Badge variant="outline" className="hover:bg-muted cursor-pointer gap-1 font-normal">
      <span className="flex flex-col items-start leading-tight">
        {name && <span>{name}</span>}
        <span className={`font-mono text-xs ${name ? 'text-muted-foreground' : ''}`}>{id}</span>
      </span>
      {canLink && <ExternalLinkIcon className="h-3 w-3 shrink-0" />}
    </Badge>
  )

  if (canLink) {
    return <Link href={href}>{content}</Link>
  }
  return content
}

// Merge resolved objects with raw IDs — resolved objects take priority, remaining IDs shown as ID-only
const mergeEntityItems = <T extends { id: string }>(
  resolved: T[] | undefined,
  rawIds: string[] | undefined,
  getLabel: (item: T) => string,
  getHref: (id: string) => string
): { id: string; name?: string; href: string }[] => {
  const resolvedMap = new Map((resolved ?? []).map(item => [item.id, item]))
  const allIds = Array.from(new Set([...(resolved ?? []).map(i => i.id), ...(rawIds ?? [])]))
  return allIds.map(id => {
    const item = resolvedMap.get(id)
    return { id, name: item ? getLabel(item) : undefined, href: getHref(id) }
  })
}

const hasFilterData = (filters: SearchLogFiltersType): boolean => {
  return !!(
    filters.categoryIds?.length ||
    filters.brandIds?.length ||
    filters.modelIds?.length ||
    filters.vehicleGroupIds?.length ||
    filters.priceMin !== undefined ||
    filters.priceMax !== undefined ||
    filters.stockMin !== undefined ||
    filters.stockMax !== undefined ||
    filters.active !== undefined ||
    filters.createdUserIds?.length ||
    filters.updatedUserIds?.length ||
    filters.createdAtStart ||
    filters.createdAtEnd ||
    filters.updatedAtStart ||
    filters.updatedAtEnd
  )
}

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const { data: searchLog, isLoading, error } = useSearchLog(id)
  const { mutate: deleteSearchLogMutate, isPending: isDeleting } = useDeleteSearchLog()
  const locale = useLocale()
  const { meState } = useMeStore()
  const canShowUser = hasPermission(meState.response, 'USERS', 'SHOW')
  const canShowCategory = hasPermission(meState.response, 'CATEGORIES', 'SHOW')
  const canShowBrand = hasPermission(meState.response, 'BRANDS', 'SHOW')
  const canShowModel = hasPermission(meState.response, 'MODELS', 'SHOW')
  const canShowVehicleGroup = hasPermission(meState.response, 'VEHICLE_GROUPS', 'SHOW')

  if (isLoading) {
    return <SearchLogShowSkeleton />
  }
  if (error || !searchLog) {
    return <ContentError error={error} listUrl="/logs/search" />
  }

  const confirmDelete = () => {
    deleteSearchLogMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['search-logs'] })
        toast.success(t('deleted_successfully'))
        router.push('/logs/search')
      },
      onError: (error: any) => {
        if (!error.response?.data?.message) {
          toast.error(t('error_deleting'))
        }
        setDeleteId(null)
      }
    })
  }

  const filters = searchLog.filters

  const categoryItems = mergeEntityItems(
    filters?.categories,
    filters?.categoryIds,
    c => getCategoryName(c, locale),
    id => `/products/categories/${id}`
  )
  const brandItems = mergeEntityItems(
    filters?.brands,
    filters?.brandIds,
    b => b.name,
    id => `/products/brands/${id}`
  )
  const modelItems = mergeEntityItems(
    filters?.models,
    filters?.modelIds,
    m => m.name,
    id => `/products/models/${id}`
  )
  const vehicleGroupItems = mergeEntityItems(
    filters?.vehicleGroups,
    filters?.vehicleGroupIds,
    vg => getVehicleGroupTitle(vg, locale),
    id => `/products/vehicle-groups/${id}`
  )
  const createdUserItems = mergeEntityItems(
    filters?.createdUsers,
    filters?.createdUserIds,
    u => u.fullName,
    id => `/users/${id}`
  )
  const updatedUserItems = mergeEntityItems(
    filters?.updatedUsers,
    filters?.updatedUserIds,
    u => u.fullName,
    id => `/users/${id}`
  )

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href="/logs/search">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('back')}</span>
              </Link>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold">
                <SearchIcon className="h-6 w-6" />
                {t('search_log')}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground font-mono text-sm">{searchLog.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(searchLog.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            <Button variant="destructive" size="sm" onClick={() => setDeleteId(id)}>
              <TrashIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('delete')}</span>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('general_information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {searchLog.q && (
                <div className="col-span-full space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <SearchIcon className="h-3.5 w-3.5" />
                    {t('search_query')}
                  </p>
                  <p className="font-medium">{searchLog.q}</p>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <HashIcon className="h-3.5 w-3.5" />
                  {t('result_count')}
                </p>
                <p className="font-mono text-sm">{searchLog.resultCount}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MonitorIcon className="h-3.5 w-3.5" />
                  {t('ip')}
                </p>
                <p className="font-mono text-sm">{searchLog.ip}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {t('searched_at')}
                </p>
                <p className="text-sm">{formatDateTime(searchLog.createdAt, locale)}</p>
              </div>

              {searchLog.userAgent && (
                <div className="col-span-full space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <GlobeIcon className="h-3.5 w-3.5" />
                    {t('user_agent')}
                  </p>
                  <p className="text-muted-foreground font-mono text-xs break-all">{searchLog.userAgent}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {searchLog.user && canShowUser && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('user_information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <UserIcon className="h-3.5 w-3.5" />
                    {t('full_name')}
                  </p>
                  <Link href={`/users/${searchLog.userId}`} className="font-medium hover:underline">
                    {searchLog.user.fullName}
                  </Link>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <WifiIcon className="h-3.5 w-3.5" />
                    {t('status')}
                  </p>
                  <Badge variant={searchLog.user.online ? 'success' : 'secondary'}>
                    {searchLog.user.online ? (
                      <>
                        <WifiIcon className="mr-1 h-3 w-3" />
                        {t('online')}
                      </>
                    ) : (
                      <>
                        <WifiOffIcon className="mr-1 h-3 w-3" />
                        {t('offline')}
                      </>
                    )}
                  </Badge>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {t('timezone')}
                  </p>
                  <p className="text-sm">{searchLog.user.timezone}</p>
                </div>

                {searchLog.user.roles.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1 text-sm">
                      <ShieldIcon className="h-3.5 w-3.5" />
                      {t('roles')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {searchLog.user.roles.map(role => (
                        <Badge key={role} variant="outline" className="font-mono text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {filters && hasFilterData(filters) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('applied_filters')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {categoryItems.length > 0 && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <TagIcon className="h-3.5 w-3.5" />
                    {t('categories')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {categoryItems.map(item => (
                      <EntityBadge
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        href={item.href}
                        canLink={canShowCategory}
                      />
                    ))}
                  </div>
                </div>
              )}

              {brandItems.length > 0 && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <TagIcon className="h-3.5 w-3.5" />
                    {t('brands')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {brandItems.map(item => (
                      <EntityBadge
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        href={item.href}
                        canLink={canShowBrand}
                      />
                    ))}
                  </div>
                </div>
              )}

              {modelItems.length > 0 && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <TagIcon className="h-3.5 w-3.5" />
                    {t('models')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {modelItems.map(item => (
                      <EntityBadge
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        href={item.href}
                        canLink={canShowModel}
                      />
                    ))}
                  </div>
                </div>
              )}

              {vehicleGroupItems.length > 0 && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <TagIcon className="h-3.5 w-3.5" />
                    {t('vehicle_groups')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {vehicleGroupItems.map(item => (
                      <EntityBadge
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        href={item.href}
                        canLink={canShowVehicleGroup}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(filters.priceMin !== undefined || filters.priceMax !== undefined) && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('price_range')}</p>
                  <p className="text-sm">
                    {filters.priceMin ?? '-'} — {filters.priceMax ?? '-'}
                  </p>
                </div>
              )}

              {(filters.stockMin !== undefined || filters.stockMax !== undefined) && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('stock_range')}</p>
                  <p className="text-sm">
                    {filters.stockMin ?? '-'} — {filters.stockMax ?? '-'}
                  </p>
                </div>
              )}

              {filters.active !== undefined && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('status')}</p>
                  <Badge variant={filters.active ? 'success' : 'secondary'}>
                    {filters.active ? t('active') : t('inactive')}
                  </Badge>
                </div>
              )}

              {(filters.createdAtStart || filters.createdAtEnd) && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('created_at_range')}</p>
                  <p className="text-sm">
                    {filters.createdAtStart ? formatDateTime(filters.createdAtStart, locale) : '-'} —{' '}
                    {filters.createdAtEnd ? formatDateTime(filters.createdAtEnd, locale) : '-'}
                  </p>
                </div>
              )}

              {(filters.updatedAtStart || filters.updatedAtEnd) && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('updated_at_range')}</p>
                  <p className="text-sm">
                    {filters.updatedAtStart ? formatDateTime(filters.updatedAtStart, locale) : '-'} —{' '}
                    {filters.updatedAtEnd ? formatDateTime(filters.updatedAtEnd, locale) : '-'}
                  </p>
                </div>
              )}

              {(createdUserItems.length > 0 || updatedUserItems.length > 0) && (
                <>
                  <Separator />
                  {createdUserItems.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground flex items-center gap-1 text-sm">
                        <UserIcon className="h-3.5 w-3.5" />
                        {t('created_by')}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {createdUserItems.map(item => (
                          <EntityBadge
                            key={item.id}
                            id={item.id}
                            name={item.name}
                            href={item.href}
                            canLink={canShowUser}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {updatedUserItems.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground flex items-center gap-1 text-sm">
                        <UserIcon className="h-3.5 w-3.5" />
                        {t('updated_by')}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {updatedUserItems.map(item => (
                          <EntityBadge
                            key={item.id}
                            id={item.id}
                            name={item.name}
                            href={item.href}
                            canLink={canShowUser}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <DeleteConfirmationDialog
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </>
  )
}

export default Detail
